import { gql } from "@apollo/client";
import { DocumentNode } from "graphql";

import { USER_FRAGMENT } from "~apollo/graphql-gateway/fragments/userFragments";

const getUserInfo: DocumentNode = gql`
  query getUser($id: ID) {
    User(filter: { id: $id }) {
      ...UserFragment
    }
  }
  ${USER_FRAGMENT}
`;

const updateUserInfo: DocumentNode = gql`
  mutation updateUserInfo($firstName: String, $lastName: String) {
    User {
      update(firstName: $firstName, lastName: $lastName) {
        ...UserFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

const requestEmailChange: DocumentNode = gql`
  mutation requestEmailChange($accountEmail: String!) {
    User {
      requestEmailChange(accountEmail: $accountEmail) {
        message
      }
    }
  }
`;

const confirmEmailChange: DocumentNode = gql`
  mutation confirmEmailChange($token: String!) {
    User {
      confirmEmailChange(token: $token) {
        ...UserFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

const deleteUser: DocumentNode = gql`
  mutation deleteUser($userID: ID) {
    User {
      remove(id: $userID) {
        message
      }
    }
  }
`;

const confirmEmailNotExist: DocumentNode = gql`
  mutation confirmEmailDoesNotExist($accountEmail: String) {
    User {
      confirmEmailDoesNotExist(accountEmail: $accountEmail) {
        message
      }
    }
  }
`;

const guestVerify: DocumentNode = gql`
  mutation guestVerify(
    $accountEmail: String
    $firstName: String
    $lastName: String
  ) {
    User {
      guestVerify(
        accountEmail: $accountEmail
        firstName: $firstName
        lastName: $lastName
      ) {
        ...UserFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;
const createGuestUser: DocumentNode = gql`
  mutation createGuest {
    User {
      createGuest {
        user {
          id
          createdDate
        }
        magicToken
      }
    }
  }
`;

const getUserShareStatus: DocumentNode = gql`
  query($id: ID) {
    User(filter: { id: $id }) {
      indeedShareStatus
    }
  }
`;

const connectOtherUserAccounts: DocumentNode = gql`
  mutation connectOtherUserAccounts($accountEmail: String, $userID: ID) {
    User(id: $userID) {
      connectOtherUserAccounts(accountEmail: $accountEmail) {
        message
      }
    }
  }
`;

const initIndeedOAuth: DocumentNode = gql`
  mutation initIndeedOAuth($from: String) {
    User {
      initIndeedOAuth(from: $from) {
        authUrl
      }
    }
  }
`;

const signInWithIndeed: DocumentNode = gql`
  mutation signInWithIndeed($code: String, $state: String) {
    User {
      signInWithIndeed(code: $code, state: $state) {
        magicToken
        indeedEmail
      }
    }
  }
`;

const googleLogin: DocumentNode = gql`
  mutation googleLogin($idToken: String, $siwi: Boolean) {
    User {
      googleLogin(idToken: $idToken, siwi: $siwi) {
        magicToken
      }
    }
  }
`;

const appleLogin: DocumentNode = gql`
  mutation appleLogin($idToken: String, $siwi: Boolean) {
    User {
      appleLogin(idToken: $idToken, siwi: $siwi) {
        magicToken
      }
    }
  }
`;

const connectUserAccount: DocumentNode = gql`
  mutation connectUserAccount($userID: ID, $connectToken: String) {
    User(id: $userID) {
      connectUserAccount(connectToken: $connectToken) {
        ...UserFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

const connectWithIndeed: DocumentNode = gql`
  mutation connectWithIndeed($userID: ID, $code: String, $state: String) {
    User(id: $userID) {
      connectWithIndeed(code: $code, state: $state) {
        message
      }
    }
  }
`;

const contactUs: DocumentNode = gql`
  mutation contactUs(
    $userEmail: String!
    $fullName: String!
    $subject: String!
    $message: String!
  ) {
    User {
      contactUs(
        userEmail: $userEmail
        fullName: $fullName
        subject: $subject
        message: $message
      ) {
        message
      }
    }
  }
`;

const ShareResumeToIndeed: DocumentNode = gql`
  mutation ShareResumeToIndeed(
    $id: ID
    $appId: String
    $isPrivate: Boolean
    $sharedResumeHash: String
  ) {
    User {
      shareWithIndeed(
        id: $id
        appId: $appId
        isPrivate: $isPrivate
        sharedResumeHash: $sharedResumeHash
      ) {
        message
      }
    }
  }
`;

const getIndeedProfile: DocumentNode = gql`
  mutation getIndeedProfile {
    User {
      getIndeedProfile {
        profile {
          resume {
            id
            source
            visibility
          }
        }
      }
    }
  }
`;

const submitInterviewForm: DocumentNode = gql`
  mutation submitInterviewForm(
    $formDataJson: String!
    $decision: String!
    $source: String
    $cohort: String
    $aiawToken: String
  ) {
    User {
      submitInterviewForm(
        formDataJson: $formDataJson
        decision: $decision
        source: $source
        cohort: $cohort
        aiawToken: $aiawToken
      ) {
        message
      }
    }
  }
`;

const cancelInterviewForm: DocumentNode = gql`
  mutation cancelInterviewForm {
    User {
      cancelInterviewForm {
        message
      }
    }
  }
`;

const setInterviewEmailSubscription: DocumentNode = gql`
  mutation setInterviewEmailSubscription($isEmailUnsubscribed: Boolean!) {
    User {
      setInterviewEmailSubscription(isEmailUnsubscribed: $isEmailUnsubscribed) {
        message
      }
    }
  }
`;

const getInterviewForm: DocumentNode = gql`
  query getInterviewForm {
    User {
      getInterviewForm {
        formDataJson
        price
        decision
        isEmailUnsubscribed
        testType
      }
    }
  }
`;

export const userQueries = {
  getUserShareStatus,
  getInterviewForm
};

export const userMutations = {
  updateUserInfo,
  requestEmailChange,
  confirmEmailChange,
  deleteUser,
  confirmEmailNotExist,
  guestVerify,
  createGuestUser,
  getUserInfo,
  connectOtherUserAccounts,
  initIndeedOAuth,
  signInWithIndeed,
  googleLogin,
  appleLogin,
  connectUserAccount,
  connectWithIndeed,
  contactUs,
  ShareResumeToIndeed,
  getIndeedProfile,
  submitInterviewForm,
  cancelInterviewForm,
  setInterviewEmailSubscription,
  getInterviewForm
};
